import { useQuery, useQueryClient } from '@tanstack/react-query';
import { handleApiResponse } from '@/functions';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import { IUserData } from '@/lib/interface';
import routes from '@/lib/routes';
import dayjs from 'dayjs';

type TParams = {
  userId: string;
  config?: {
    staleTime?: number;
  }
};
type TResponseData = IUserData[];

export const useRequestGetUserSubAccounts = (params: TParams, run = true) => {

  const queryClient = useQueryClient();

  const staleTime = params?.config?.staleTime ?? queryClient.getDefaultOptions().queries?.staleTime as any;

  if (params?.config) {
    delete params.config;
  }

  const { invalidate } = useInvalidateQueries();
  const { isFetching, data, error, dataUpdatedAt } = useQuery({
    queryKey: [routes.api.subAccounts, params, run],
    queryFn: ({ queryKey: [_url, _params, _run] }) =>
      !_run || !(_params as TParams)?.userId
        ? Promise.resolve(null)
        : fetchQuery(_url as string, _params as TParams),
    staleTime
  });

  const subAccounts = (data ?? []);

  const fetchQuery = (url: string, params: TParams) =>
    apiClient
      .get<TResponseData>(url, params)
      .then(handleApiResponse)
      .then(
        data =>
          data
            ? [...data].map(l => ({ ...l, createdAt: dayjs(l.createdAt).toDate() }))
            : data
      );

  const fetchSubAccounts = (params: TParams) =>
    fetchQuery(routes.api.subAccounts, params);

  const refresh = () =>
    invalidate(routes.api.subAccounts);

  return ({
    subAccountsUpdatedAt: dataUpdatedAt,
    isLoadingSubAccounts: isFetching,
    fetchSubAccounts,
    subAccounts,
    refresh,
    error
  });

};
